import {
  alpha,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import { styled } from '@mui/material/styles';
import { useFormik } from "formik";
import React, { memo, useCallback, useEffect, useState } from "react";
import * as Yup from "yup";

// Icons
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import EmailIcon from '@mui/icons-material/Email';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import GroupIcon from '@mui/icons-material/Group';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import PublicIcon from '@mui/icons-material/Public';
import SaveIcon from '@mui/icons-material/Save';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import api from "../../services/api";
import ToastError from "../../toast/error/toastError";
import ToastSuccess from "../../toast/success/toastSuccess";

// Lista de estados brasileiros agrupados por região
const RegioesBrasil = {
  Norte: [
    { uf: "AC", nome: "Acre" },
    { uf: "AP", nome: "Amapá" },
    { uf: "AM", nome: "Amazonas" },
    { uf: "PA", nome: "Pará" },
    { uf: "RO", nome: "Rondônia" },
    { uf: "RR", nome: "Roraima" },
    { uf: "TO", nome: "Tocantins" },
  ],
  Nordeste: [
    { uf: "AL", nome: "Alagoas" },
    { uf: "BA", nome: "Bahia" },
    { uf: "CE", nome: "Ceará" },
    { uf: "MA", nome: "Maranhão" },
    { uf: "PB", nome: "Paraíba" },
    { uf: "PE", nome: "Pernambuco" },
    { uf: "PI", nome: "Piauí" },
    { uf: "RN", nome: "Rio Grande do Norte" },
    { uf: "SE", nome: "Sergipe" },
  ],
  CentroOeste: [
    { uf: "DF", nome: "Distrito Federal" },
    { uf: "GO", nome: "Goiás" },
    { uf: "MT", nome: "Mato Grosso" },
    { uf: "MS", nome: "Mato Grosso do Sul" },
  ],
  Sudeste: [
    { uf: "ES", nome: "Espírito Santo" },
    { uf: "MG", nome: "Minas Gerais" },
    { uf: "RJ", nome: "Rio de Janeiro" },
    { uf: "SP", nome: "São Paulo" },
  ],
  Sul: [
    { uf: "PR", nome: "Paraná" },
    { uf: "RS", nome: "Rio Grande do Sul" },
    { uf: "SC", nome: "Santa Catarina" },
  ]
};

// Lista plana de todos os estados para uso em operações de busca
const Estados = Object.values(RegioesBrasil).flat();

// Funções de validação de CPF e CNPJ
const isCPF = (cpf) => {
  if (!cpf) return false;

  // Remove caracteres não numéricos
  cpf = cpf.replace(/[^\d]/g, '');

  // Verifica se o CPF tem 11 dígitos
  if (cpf.length !== 11) return false;

  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(cpf)) return false;

  // Validação do primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cpf.charAt(i)) * (10 - i);
  }
  let remainder = sum % 11;
  let digit1 = remainder < 2 ? 0 : 11 - remainder;

  // Validação do segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cpf.charAt(i)) * (11 - i);
  }
  remainder = sum % 11;
  let digit2 = remainder < 2 ? 0 : 11 - remainder;

  // Verifica se os dígitos calculados são iguais aos dígitos informados
  return (parseInt(cpf.charAt(9)) === digit1 && parseInt(cpf.charAt(10)) === digit2);
};

const isCNPJ = (cnpj) => {
  if (!cnpj) return false;

  // Remove caracteres não numéricos
  cnpj = cnpj.replace(/[^\d]/g, '');

  // Verifica se o CNPJ tem 14 dígitos
  if (cnpj.length !== 14) return false;

  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(cnpj)) return false;

  // Validação do primeiro dígito verificador
  let size = cnpj.length - 2;
  let numbers = cnpj.substring(0, size);
  const digits = cnpj.substring(size);
  let sum = 0;
  let pos = size - 7;

  for (let i = size; i >= 1; i--) {
    sum += parseInt(numbers.charAt(size - i)) * pos--;
    if (pos < 2) pos = 9;
  }

  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0))) return false;

  // Validação do segundo dígito verificador
  size = size + 1;
  numbers = cnpj.substring(0, size);
  sum = 0;
  pos = size - 7;

  for (let i = size; i >= 1; i--) {
    sum += parseInt(numbers.charAt(size - i)) * pos--;
    if (pos < 2) pos = 9;
  }

  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);

  return (result === parseInt(digits.charAt(1)));
};

// Formatar CPF/CNPJ
const formatCpfCnpj = (value) => {
  if (!value) return '';

  // Remove todos os caracteres não numéricos
  const numbers = value.replace(/\D/g, '');

  // Formata como CPF se tiver até 11 dígitos
  if (numbers.length <= 11) {
    return numbers
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  }
  // Formata como CNPJ se tiver mais de 11 dígitos
  else {
    return numbers
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  }
};

// Formatar telefone
const formatPhone = (value) => {
  if (!value) return '';

  // Remove todos os caracteres não numéricos
  const numbers = value.replace(/\D/g, '');

  if (numbers.length <= 10) {
    return numbers
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  } else {
    return numbers
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2');
  }
};

// Styled Components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '100%',
    maxWidth: 900,
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
      : '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(1),
      maxHeight: `calc(100% - ${theme.spacing(2)})`,
    },
  },
  '& .MuiDialogTitle-root': {
    padding: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3),
    },
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(3),
    },
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.5) : alpha(theme.palette.background.default, 0.8),
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.grey[600], 0.8) : theme.palette.grey[400],
      borderRadius: '4px',
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[500] : theme.palette.grey[500],
      }
    },
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1.5, 2),
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(2, 3),
    },
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(5px)',
    backgroundColor: alpha(theme.palette.mode === 'dark' ? '#000' : '#000', 0.4),
  }
}));

const StatusBadge = styled(Box)(({ theme, active }) => ({
  padding: theme.spacing(0.25, 1),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(0.5, 1.5),
  },
  borderRadius: 20,
  backgroundColor: active ? theme.palette.success.main : theme.palette.error.main,
  color: theme.palette.common.white,
  fontSize: '0.75rem',
  fontWeight: 600,
  boxShadow: theme.shadows[1],
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    transition: 'all 0.2s ease-out',
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.05)
      : alpha(theme.palette.common.black, 0.02),
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.08)
        : alpha(theme.palette.common.black, 0.04),
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.07)
        : theme.palette.common.white,
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.4 : 0.25)}`,
    }
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.15)
      : alpha(theme.palette.common.black, 0.1),
  },
  '& .MuiFormHelperText-root': {
    marginLeft: 4,
    fontSize: '0.65rem',
  }
}));

const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 8,
    transition: 'all 0.2s ease-out',
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.05)
      : alpha(theme.palette.common.black, 0.02),
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.08)
        : alpha(theme.palette.common.black, 0.04),
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.07)
        : theme.palette.common.white,
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.4 : 0.25)}`,
    }
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.15)
      : alpha(theme.palette.common.black, 0.1),
  },
  '& .MuiAutocomplete-listbox': {
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: theme.palette.mode === 'dark' 
        ? alpha(theme.palette.background.default, 0.5) 
        : alpha(theme.palette.background.default, 0.8),
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.mode === 'dark' 
        ? alpha(theme.palette.grey[600], 0.8) 
        : theme.palette.grey[400],
      borderRadius: '4px',
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark' 
          ? theme.palette.grey[500] 
          : theme.palette.grey[500],
      }
    }
  },
  '& .MuiAutocomplete-groupLabel': {
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.dark, 0.15)
      : alpha(theme.palette.primary.light, 0.12),
    padding: '4px 12px',
    fontWeight: 600,
    fontSize: '0.7rem',
    color: theme.palette.mode === 'dark'
      ? theme.palette.primary.light
      : theme.palette.primary.dark,
  },
  '& .MuiAutocomplete-groupUl': {
    padding: '4px 0',
  },
  '& .MuiAutocomplete-option': {
    paddingTop: 6,
    paddingBottom: 6,
    minHeight: 42,
  },
  '& .MuiAutocomplete-paper': {
    boxShadow: theme.shadows[8],
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: 8,
  padding: theme.spacing(0.5, 1.5),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(0.75, 2),
  },
  fontWeight: 600,
  boxShadow: 'none',
  fontSize: '0.8rem',
  '&:hover': {
    boxShadow: theme.shadows[1],
  }
}));

const StyledSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgba(0,0,0,0.2)',
  },
  '& .MuiSwitch-track': {
    backgroundColor: alpha(theme.palette.mode === 'dark' ? '#fff' : '#000', 0.2),
    opacity: 1,
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '0.85rem',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1.5),
  paddingBottom: theme.spacing(0.75),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
}));

// Componente de CPF/CNPJ separado para evitar re-renders desnecessários
const DocumentField = memo(({ value, onChange, error, helperText, isValid }) => {
  const theme = useTheme();
  const documentType = value && value.replace(/\D/g, '').length <= 11 ? 'CPF' : 'CNPJ';
  
  return (
    <StyledTextField
      fullWidth
      size="small"
      name="cnpj"
      label={documentType}
      value={value}
      onChange={onChange}
      error={error}
      helperText={helperText}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <DescriptionIcon fontSize="small" color="action" />
          </InputAdornment>
        ),
        endAdornment: value && (
          <InputAdornment position="end">
            {isValid ? (
              <Tooltip title="Documento válido">
                <CheckCircleOutlineIcon fontSize="small" color="success" />
              </Tooltip>
            ) : (
              <Tooltip title="Documento inválido">
                <ErrorOutlineIcon fontSize="small" color="error" />
              </Tooltip>
            )}
          </InputAdornment>
        ),
      }}
    />
  );
});

// Valores iniciais definidos como constante para garantir consistência
const INITIAL_VALUES = {
  name: "",
  cnpj: "",
  maxWhatsapps: "",
  maxUsers: "",
  email: "",
  contractor: "",
  phone: "",
  city: "",
  state: "",
  address: "",
  isActive: true
};

// Componente principal
const CreateOrEditCompanyModal = ({ open, onClose, companyId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [documentType, setDocumentType] = useState('cnpj');
  const [documentValid, setDocumentValid] = useState(false);
  
  // Funções para lidar com o formato e validação do CPF/CNPJ
  const validateDocument = useCallback((value) => {
    if (!value) return false;

    // Remove caracteres não numéricos para a validação
    const cleanValue = value.replace(/\D/g, '');

    // Identifica se é CPF ou CNPJ pelo número de dígitos
    if (cleanValue.length <= 11) {
      setDocumentType('cpf');
      const isValid = isCPF(cleanValue);
      setDocumentValid(isValid);
      return isValid;
    } else {
      setDocumentType('cnpj');
      const isValid = isCNPJ(cleanValue);
      setDocumentValid(isValid);
      return isValid;
    }
  }, []);

  // Esquema de validação com Yup
  const validationSchema = Yup.object({
    name: Yup.string()
      .required("O nome da empresa é obrigatório")
      .min(3, "Nome deve ter pelo menos 3 caracteres"),
    cnpj: Yup.string()
      .required("O CPF/CNPJ é obrigatório")
      .test('is-cpf-cnpj', documentType === 'cpf' ? 'CPF inválido' : 'CNPJ inválido',
        value => validateDocument(value)),
    maxWhatsapps: Yup.number()
      .required("O máximo de WhatsApps é obrigatório")
      .min(1, "Deve permitir pelo menos 1 WhatsApp")
      .integer("Deve ser um número inteiro"),
    maxUsers: Yup.number()
      .required("O máximo de usuários é obrigatório")
      .min(1, "Deve permitir pelo menos 1 usuário")
      .integer("Deve ser um número inteiro"),
    email: Yup.string()
      .email("E-mail inválido")
      .required("O e-mail é obrigatório"),
    contractor: Yup.string()
      .required("O responsável é obrigatório")
      .min(3, "Nome deve ter pelo menos 3 caracteres"),
    phone: Yup.string()
      .required("O contato é obrigatório")
      .min(14, "Número de telefone incompleto"),
    city: Yup.string()
      .required("A cidade é obrigatória")
      .min(2, "Nome da cidade muito curto"),
    state: Yup.string()
      .required("O estado é obrigatório"),
    address: Yup.string()
      .required("O endereço é obrigatório")
      .min(5, "Endereço deve ser mais detalhado"),
  });

  const formik = useFormik({
    initialValues: INITIAL_VALUES,
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        if (companyId) {
          await api.put(`/company/${companyId}`, values);
          ToastSuccess("Empresa atualizada com sucesso!");
        } else {
          await api.post("/company", values);
          ToastSuccess("Empresa cadastrada com sucesso!");
        }
        handleClose();
      } catch (error) {
        ToastError(error?.response?.data?.message || "Erro ao salvar empresa");
      } finally {
        setLoading(false);
      }
    },
  });

  // Função para reiniciar o formulário - CORRIGIDA
  const resetForm = useCallback(() => {
    setDocumentType('cnpj');
    setDocumentValid(false);
    setLoading(false);
  }, []);

  // Efeito para carregar dados da empresa quando o modal abre - SIMPLIFICADO
  useEffect(() => {
    if (!open) return;

    if (companyId) {
      // Modo edição: carrega os dados da empresa
      const fetchCompany = async () => {
        setLoading(true);
        try {
          const { data } = await api.get(`/company/${companyId}`);

          // Formata o CPF/CNPJ e telefone antes de preencher o formulário
          const formattedCnpj = formatCpfCnpj(data.cnpj);
          const formattedPhone = formatPhone(data.phone);

          formik.setValues({
            ...data,
            cnpj: formattedCnpj,
            phone: formattedPhone
          });

          // Define o tipo de documento para validação adequada e valida
          validateDocument(formattedCnpj);
        } catch (error) {
          ToastError("Erro ao carregar dados da empresa");
          formik.resetForm();
        } finally {
          setLoading(false);
        }
      };

      fetchCompany();
    } else {
      // Modo criação: limpa o formulário
      formik.resetForm();
      resetForm();
    }
  }, [open, companyId]); // Simplificado: apenas open e companyId como dependências

  const handleClose = () => {
    formik.resetForm();
    resetForm();
    onClose();
  };

  // Manipuladores para formatação de entrada
  const handleCpfCnpjChange = useCallback((e) => {
    const rawValue = e.target.value.replace(/\D/g, '');
    const formattedValue = formatCpfCnpj(rawValue);
    formik.setFieldValue('cnpj', formattedValue);
    validateDocument(formattedValue);
  }, [formik, validateDocument]);

  const handlePhoneChange = useCallback((e) => {
    const formattedValue = formatPhone(e.target.value);
    formik.setFieldValue('phone', formattedValue);
  }, [formik]);

  return (
    <StyledDialog
      open={open}
      onClose={loading ? undefined : handleClose}
      scroll="paper"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          gap: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <BusinessIcon
              sx={{
                mr: isMobile ? 0.5 : 1,
                color: theme.palette.primary.main,
                fontSize: isMobile ? 22 : 28
              }}
            />
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight={700}>
              {companyId ? "Editar Empresa" : "Adicionar Empresa"}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            {companyId && (
              <StatusBadge active={formik.values.isActive}>
                {formik.values.isActive
                  ? <><CheckCircleOutlineIcon fontSize="small" /> {!isMobile && "Ativa"}</>
                  : <><ErrorOutlineIcon fontSize="small" /> {!isMobile && "Bloqueada"}</>
                }
              </StatusBadge>
            )}

            <IconButton
              edge="end"
              color="inherit"
              onClick={handleClose}
              aria-label="close"
              disabled={loading}
              size="small"
              sx={{
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.text.secondary, 0.1),
                },
              }}
            >
              <CloseIcon fontSize={isMobile ? "small" : "medium"} />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <Divider />

      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Grid container spacing={isMobile ? 2 : 3}>
            {/* Seção de informações básicas */}
            <Grid item xs={12}>
              <SectionTitle>
                Informações Básicas
              </SectionTitle>
            </Grid>

            <Grid item xs={12} sm={8}>
              <StyledTextField
                fullWidth
                size="small"
                name="name"
                label="Nome da Empresa"
                value={formik.values.name}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <DocumentField 
                value={formik.values.cnpj}
                onChange={handleCpfCnpjChange}
                error={formik.touched.cnpj && Boolean(formik.errors.cnpj)}
                helperText={formik.touched.cnpj && formik.errors.cnpj}
                isValid={documentValid}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <StyledTextField
                fullWidth
                size="small"
                name="contractor"
                label="Responsável"
                value={formik.values.contractor}
                onChange={formik.handleChange}
                error={formik.touched.contractor && Boolean(formik.errors.contractor)}
                helperText={formik.touched.contractor && formik.errors.contractor}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={6} sm={4}>
              <StyledTextField
                fullWidth
                size="small"
                type="number"
                name="maxWhatsapps"
                label="Máx. WhatsApps"
                value={formik.values.maxWhatsapps}
                onChange={formik.handleChange}
                error={formik.touched.maxWhatsapps && Boolean(formik.errors.maxWhatsapps)}
                helperText={formik.touched.maxWhatsapps && formik.errors.maxWhatsapps}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <WhatsAppIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={6} sm={4}>
              <StyledTextField
                fullWidth
                size="small"
                type="number"
                name="maxUsers"
                label="Máx. Usuários"
                value={formik.values.maxUsers}
                onChange={formik.handleChange}
                error={formik.touched.maxUsers && Boolean(formik.errors.maxUsers)}
                helperText={formik.touched.maxUsers && formik.errors.maxUsers}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <GroupIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Seção de Contato */}
            <Grid item xs={12}>
              <SectionTitle>
                Informações de Contato
              </SectionTitle>
            </Grid>

            <Grid item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                size="small"
                name="phone"
                label="Telefone"
                value={formik.values.phone}
                onChange={handlePhoneChange}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                size="small"
                name="email"
                label="E-mail"
                type="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Seção de Endereço */}
            <Grid item xs={12}>
              <SectionTitle>
                Endereço
              </SectionTitle>
            </Grid>

            <Grid item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                size="small"
                name="address"
                label="Endereço"
                value={formik.values.address}
                onChange={formik.handleChange}
                error={formik.touched.address && Boolean(formik.errors.address)}
                helperText={formik.touched.address && formik.errors.address}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOnIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={6} sm={3}>
              <StyledTextField
                fullWidth
                size="small"
                name="city"
                label="Cidade"
                value={formik.values.city}
                onChange={formik.handleChange}
                error={formik.touched.city && Boolean(formik.errors.city)}
                helperText={formik.touched.city && formik.errors.city}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationCityIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={6} sm={3}>
              <FormControl fullWidth error={formik.touched.state && Boolean(formik.errors.state)} size="small">
                <StyledAutocomplete
                  id="state-autocomplete"
                  size="small"
                  options={Estados}
                  groupBy={(option) => {
                    // Encontrar a região do estado
                    for (const [regiao, estados] of Object.entries(RegioesBrasil)) {
                      if (estados.some(estado => estado.uf === option.uf)) {
                        switch (regiao) {
                          case 'Norte': return 'Norte';
                          case 'Nordeste': return 'Nordeste';
                          case 'CentroOeste': return 'Centro-Oeste';
                          case 'Sudeste': return 'Sudeste';
                          case 'Sul': return 'Sul';
                          default: return regiao;
                        }
                      }
                    }
                    return '';
                  }}
                  getOptionLabel={(option) => typeof option === 'string' ? option : option.nome}
                  value={Estados.find(e => e.uf === formik.values.state) || null}
                  onChange={(_, newValue) => {
                    formik.setFieldValue('state', newValue ? newValue.uf : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Estado"
                      size="small"
                      error={formik.touched.state && Boolean(formik.errors.state)}
                      helperText={formik.touched.state && formik.errors.state}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PublicIcon fontSize="small" color="action" />
                          </InputAdornment>
                        ),
                        ...params.InputProps,
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} style={{ padding: '4px 12px' }}>
                      {option.nome} ({option.uf})
                    </li>
                  )}
                  ListboxProps={{
                    style: { 
                      maxHeight: '250px',
                      padding: '4px 0'
                    }
                  }}
                  renderTags={(value, getTagProps) => 
                    value.map((option, index) => (
                      <div {...getTagProps({ index })}>
                        {option.uf}
                      </div>
                    ))
                  }
                />
              </FormControl>
            </Grid>

            {/* Opção para ativar/desativar a empresa (só em edição) */}
            {companyId && (
              <Grid item xs={12}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: isMobile ? 1.5 : 2,
                  borderRadius: 2,
                  backgroundColor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.primary.main, 0.1)
                    : alpha(theme.palette.primary.main, 0.05)
                }}>
                  <FormControlLabel
                    control={
                      <StyledSwitch
                        size="small"
                        checked={formik.values.isActive}
                        onChange={(e) => formik.setFieldValue('isActive', e.target.checked)}
                        name="isActive"
                        color="primary"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="subtitle2" fontWeight={600} fontSize={isMobile ? "0.8rem" : "0.875rem"}>
                          {formik.values.isActive ? "Empresa Ativa" : "Empresa Bloqueada"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" fontSize={isMobile ? "0.65rem" : "0.75rem"}>
                          {formik.values.isActive
                            ? "A empresa tem acesso à plataforma"
                            : "A empresa não pode acessar a plataforma"}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            width: '100%',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1 : 0
          }}>
            <StyledButton
              onClick={handleClose}
              color="inherit"
              variant="outlined"
              disabled={loading}
              startIcon={<CloseIcon fontSize="small" />}
              fullWidth={isMobile}
              size="small"
            >
              Cancelar
            </StyledButton>

            <Box sx={{ position: 'relative', width: isMobile ? '100%' : 'auto' }}>
              <StyledButton
                type="submit"
                color="primary"
                variant="contained"
                disabled={loading}
                startIcon={companyId ? <SaveIcon fontSize="small" /> : <AddCircleOutlineIcon fontSize="small" />}
                fullWidth={isMobile}
                size="small"
              >
                {companyId ? "Salvar Alterações" : "Adicionar Empresa"}
              </StyledButton>
              {loading && (
                <CircularProgress
                  size={20}
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-10px',
                    marginLeft: '-10px',
                  }}
                />
              )}
            </Box>
          </Box>
        </DialogActions>
      </form>
    </StyledDialog>
  );
};

export default CreateOrEditCompanyModal;